import { computed, DestroyRef, inject, Injectable, NgZone, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DefaultLocale } from '@app/+authenticated/shared/locale/locale.helper';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { accountModuleStore } from '@reducers/account-module/account-module.state';
import { selectAuthUserId } from '@reducers/auth/auth.selectors';
import { AppState } from '@reducers/interfaces';
import { catchError, distinctUntilChanged, filter, from, map, Observable, of, skip, take } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Channel, OwnUserResponse } from 'stream-chat';
import { ChannelService, ChatClientService, MessageActionsService } from 'stream-chat-angular';

import { ChatTokenApiService } from './chat-token-api.service';
import { STREAM_CHAT_TRANSLATIONS } from './stream-chat-translations';

export type StreamChatState = 'idle' | 'connecting' | 'connected' | 'error' | 'unavailable';

@Injectable({ providedIn: 'root' })
export class StreamChatService {
  #store = inject<Store<AppState>>(Store);
  #chatClientService = inject(ChatClientService);
  #channelService = inject(ChannelService);
  #chatTokenApi = inject(ChatTokenApiService);
  #translate = inject(TranslateService);
  #destroyRef = inject(DestroyRef);
  #ngZone = inject(NgZone);
  #messageActionsService = inject(MessageActionsService);

  #state = signal<StreamChatState>('idle');
  state = this.#state.asReadonly();

  #chatUserId = signal<string | null>(null);
  chatUserId = this.#chatUserId.asReadonly();

  #channels = toSignal(this.#channelService.channels$, { initialValue: undefined });
  channelsLoaded = computed(() => this.#channels() !== undefined);
  hasChannels = computed(() => {
    const channels = this.#channels();
    return (channels?.length ?? 0) > 0;
  });

  #searchResults = signal<Channel[] | null>(null);
  searchResults = this.#searchResults.asReadonly();

  activeChannel = toSignal(this.#channelService.activeChannel$, { initialValue: undefined });

  #activeChannelMessages = toSignal(this.#channelService.activeChannelMessages$, { initialValue: [] });
  activeChannelHasMessages = computed(() => this.#activeChannelMessages().length > 0);

  #totalUnreadCount = signal(0);
  totalUnreadCount = this.#totalUnreadCount.asReadonly();

  #channelsRequested = false;
  #chatPageActive = false;

  constructor() {
    this.#store
      .select(selectAuthUserId)
      .pipe(takeUntilDestroyed())
      .subscribe((userId) => {
        if (userId && this.#state() === 'idle') {
          this.#fetchTokenAndConnect();
        } else if (!userId && this.#state() === 'connected') {
          void this.#disconnect();
        }
      });

    this.#store
      .select(accountModuleStore.selectModuleEnabled('chat'))
      .pipe(distinctUntilChanged(), skip(1), takeUntilDestroyed())
      .subscribe((enabled) => {
        if (enabled) {
          this.retryConnection();
        } else {
          void this.#disconnect().then(() => this.#state.set('unavailable'));
        }
      });

    this.#destroyRef.onDestroy(() => {
      void this.#disconnect();
    });

    const lang = this.#translate.getCurrentLang() || this.#translate.getFallbackLang() || DefaultLocale;
    this.#translate.setTranslation(lang, STREAM_CHAT_TRANSLATIONS, true);

    this.#removeUnusedMessageActions();

    this.#chatClientService.events$
      .pipe(
        filter(
          ({ eventType }) =>
            eventType === 'message.new' ||
            eventType === 'notification.message_new' ||
            eventType === 'notification.mark_read' ||
            eventType === 'notification.mark_unread',
        ),
        filter(({ event }) => event.total_unread_count != null),
        takeUntilDestroyed(),
      )
      .subscribe(({ event }) => {
        this.#ngZone.run(() => this.#totalUnreadCount.set(event.total_unread_count));
      });
  }

  #fetchTokenAndConnect(): void {
    const apiKey = environment.streamChatApiKey;
    if (!apiKey) {
      this.#state.set('unavailable');
      return;
    }

    this.#state.set('connecting');

    this.#chatTokenApi
      .getToken()
      .pipe(
        take(1),
        catchError(() => of(null)),
      )
      .subscribe((response) => {
        if (!response?.streamToken || !response.chatUserId) {
          this.#state.set('unavailable');
          return;
        }

        this.#chatUserId.set(response.chatUserId);
        this.#connectToStream(apiKey, response.chatUserId, response.streamToken);
      });
  }

  #connectToStream(apiKey: string, userId: string, token: string): void {
    const tokenProvider = this.#createTokenProvider(token);

    void this.#chatClientService
      .init(apiKey, userId, tokenProvider)
      .then(() => {
        this.#channelService.shouldMarkActiveChannelAsRead = this.#chatPageActive;
        this.#state.set('connected');
        this.#ngZone.run(() =>
          this.#totalUnreadCount.set(
            (this.#chatClientService.chatClient.user as OwnUserResponse | undefined)?.total_unread_count ?? 0,
          ),
        );
        this.#tryInitChannels();
      })
      .catch(() => {
        this.#state.set('error');
      });
  }

  setChatPageActive(active: boolean): void {
    this.#chatPageActive = active;
    this.#channelService.shouldMarkActiveChannelAsRead = active;
  }

  initChannels(): void {
    this.#channelsRequested = true;
    this.#tryInitChannels();
  }

  #tryInitChannels(): void {
    const userId = this.#chatUserId();
    if (userId && this.#state() === 'connected' && this.#channelsRequested) {
      void this.#channelService.init({ type: 'messaging', members: { $in: [userId] }, frozen: false });
    }
  }

  retryConnection(): void {
    if (this.#state() === 'unavailable' || this.#state() === 'error') {
      this.#state.set('idle');
      this.#fetchTokenAndConnect();
    }
  }

  reloadChannels(): void {
    const userId = this.#chatUserId();
    if (userId && this.#state() === 'connected') {
      this.#channelService.reset();
      void this.#channelService.init({ type: 'messaging', members: { $in: [userId] }, frozen: false });
    }
  }

  searchChannels(searchTerm: string): Observable<void> {
    const userId = this.#chatUserId();
    if (!userId || this.#state() !== 'connected') return of(undefined);

    const trimmed = searchTerm.trim();
    if (!trimmed) {
      this.#searchResults.set(null);
      return of(undefined);
    }

    const client = this.#chatClientService.chatClient;
    if (!client) return of(undefined);

    const filter = {
      type: 'messaging',
      members: { $in: [userId] },
      frozen: false,
      name: { $autocomplete: trimmed },
    };

    return from(client.queryChannels(filter, undefined, { watch: false, state: false })).pipe(
      map((results) => {
        this.#searchResults.set(results);
      }),
      catchError(() => {
        this.#searchResults.set([]);
        return of(undefined);
      }),
    );
  }

  async #disconnect(): Promise<void> {
    if (this.#state() !== 'connected') {
      return;
    }

    await this.#chatClientService.disconnectUser();
    this.#channelsRequested = false;
    this.#chatUserId.set(null);
    this.#searchResults.set(null);
    this.#state.set('idle');
    this.#totalUnreadCount.set(0);
  }

  #removeUnusedMessageActions(): void {
    for (const name of ['pin', 'flag']) {
      const index = this.#messageActionsService.defaultActions.findIndex((a) => a.actionName === name);
      if (index !== -1) this.#messageActionsService.defaultActions.splice(index, 1);
    }
  }

  #createTokenProvider(initialToken: string): () => Promise<string> {
    let isFirstCall = true;

    return () => {
      if (isFirstCall) {
        isFirstCall = false;
        return Promise.resolve(initialToken);
      }

      return new Promise<string>((resolve, reject) => {
        this.#chatTokenApi
          .getToken()
          .pipe(take(1))
          .subscribe({
            next: (response) => {
              if (response?.streamToken) {
                resolve(response.streamToken);
              } else {
                reject(new Error('Failed to refresh stream token'));
              }
            },
            error: (error) => reject(error),
          });
      });
    };
  }
}
